import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Lock, ShieldCheck, BadgeCheck, Mail, Activity,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  NABD_FONT_AR, NABD_FONT_DISPLAY, ensureNabdFonts, nabdVisitorId,
} from "../../lib/nabdTheme";
import CheckoutTrustBlock from "../../components/CheckoutTrustBlock";
import PaymentHelpChatbot from "../../components/PaymentHelpChatbot";
import InlinePayment from "../../components/InlinePayment";
import InAppBrowserBanner from "../../components/InAppBrowserBanner";
import { localisedPrice } from "../../lib/localisedPrice";

// Shared TikTok payload + helper — same AED→USD treatment as the landing
// (99 AED → 26.73 USD; AED pegged ~3.67 to USD) so TikTok's USD-only
// dashboards aggregate cleanly with the other funnels.
const NABD_USD = Math.round(99 * 0.27 * 100) / 100;
const NABD_CONTENTS = {
  contents: [{
    content_id: "nabd_market_ai",
    content_type: "product",
    content_name: "نبض السوق AI",
    quantity: 1,
    price: NABD_USD,
  }],
  value: NABD_USD,
  currency: "USD",
};
const ttFireNabd = (eventName, opts = {}) => {
  if (typeof window === "undefined" || !window.ttq?.track) return;
  try {
    window.ttq.track(
      eventName,
      NABD_CONTENTS,
      opts.eventId ? { event_id: opts.eventId } : undefined,
    );
  } catch (_) { /* never breaks the checkout */ }
};
const newTtEid = (label) =>
  `nabd_${label}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const isValidEmail = (s) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

/**
 * Checkout confirmation page for نبض السوق AI.
 *
 * Single-bundle (99 AED, was 199). The visitor reviews the order, optionally types
 * an email (for receipt + locks Stripe email field), then completes payment
 * inline via Stripe Payment Element — no redirect to Stripe's hosted page.
 */
export default function CheckoutNabd() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  // Help-bot open state — driven by the Gulf trust-block CTA.
  const [chatOpen, setChatOpen] = useState(false);

  // Inline Stripe Payment Element state. Single bundle, so we mint a PI once
  // on mount; email changes re-mint with `receipt_email` set so Stripe pre-
  // fills the receipt automatically.
  const [clientSecret, setClientSecret] = useState(null);
  const [orderToken, setOrderToken] = useState(null);
  const [piLoading, setPiLoading] = useState(false);
  // Visitor's country (from IP geo) — passed to Stripe defaultCountry so
  // Gulf visitors don't see "Egypt" pre-selected in the billing dropdown.
  const [country, setCountry] = useState("AE");
  useEffect(() => {
    let cancelled = false;
    api.get("/geo")
      .then((r) => { if (!cancelled) {
        const c = (r.data?.country || "").toUpperCase();
        if (c && c.length === 2) setCountry(c);
      } })
      .catch(() => { /* keep AE default */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    ensureNabdFonts();
    document.title = "نبض السوق AI — إتمام الطلب";
    // TikTok InitiateCheckout — visitor landed on the order-summary page.
    ttFireNabd("InitiateCheckout", { eventId: newTtEid("initcheckout") });
  }, []);

  // Mint / re-mint the PaymentIntent. Debounced on email typing.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setPiLoading(true);
      setErr("");
      try {
        const cleanEmail = (email || "").trim().toLowerCase();
        const payload = { visitor_id: nabdVisitorId() };
        if (cleanEmail && isValidEmail(cleanEmail)) payload.email = cleanEmail;
        const { data } = await api.post("/nabd/payment-intent", payload);
        if (cancelled) return;
        setClientSecret(data.client_secret);
        setOrderToken(data.order_token);
      } catch (e2) {
        if (cancelled) return;
        const raw = e2?.response?.data?.detail;
        const status = e2?.response?.status;
        const safe = typeof raw === "string" && raw.length > 0 && raw.length < 160 && !/<[a-z!]/i.test(raw);
        const fallback = status && status >= 500
          ? "حدث خلل مؤقت في تحضير الدفع. حاول مرة أخرى بعد لحظات."
          : "تعذّر تحضير نموذج الدفع. حاول مرة أخرى.";
        setErr(safe ? raw : fallback);
      } finally {
        if (!cancelled) setPiLoading(false);
      }
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [email]);

  // Capture intent right before confirming — paired with InitiateCheckout
  // for downstream TikTok dedup.
  const fireSubmitPixel = () => {
    ttFireNabd("AddToCart", { eventId: newTtEid("addtocart-submit") });
  };

  return (
    <div
      dir="rtl"
      lang="ar"
      className="min-h-screen bg-[#06090F] text-[#E8ECEF]"
      style={{ fontFamily: NABD_FONT_AR }}
      data-testid="nabd-checkout-root"
    >
      <InAppBrowserBanner />
      <nav className="sticky top-0 z-30 bg-[#06090F]/85 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <Link to="/nabd" className="inline-flex items-center gap-2 text-[12.5px] text-[#9AA5B1] hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
            رجوع
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center border" style={{ background: "linear-gradient(180deg,#0E141C,#040608)", borderColor: "rgba(244,184,66,0.40)" }}>
              <Activity className="w-3.5 h-3.5 text-[#F4B842]" strokeWidth={2.4} />
            </div>
            <span className="font-bold text-[14px] text-white" style={{ fontFamily: NABD_FONT_DISPLAY }}>نبض السوق <span className="text-[#F4B842]">AI</span></span>
          </div>
        </div>
      </nav>

      <main className="max-w-md mx-auto px-5 py-12">
        <div className="text-[10.5px] tracking-[0.30em] text-[#F4B842] font-bold mb-3 text-center">إتمام الطلب</div>
        <h1 className="font-extrabold text-[24px] sm:text-[30px] text-white text-center leading-tight mb-2" style={{ fontFamily: NABD_FONT_DISPLAY }}>
          خطوة واحدة وتصلك التجربة.
        </h1>
        <p className="text-[13.5px] text-[#9AA5B1] text-center leading-[2] mb-9">
          إتمام دفع آمن على هذه الصفحة. الوصول للتطبيق فوري بعد الدفع.
        </p>

        {/* Order card */}
        <div
          className="rounded-2xl p-6 border"
          style={{ background: "linear-gradient(180deg,#0E141C,#040608)", borderColor: "rgba(244,184,66,0.30)" }}
          data-testid="nabd-checkout-summary"
        >
          <div className="flex items-start justify-between gap-4 pb-5 border-b border-white/[0.06]">
            <div>
              <div className="text-[10.5px] tracking-[0.22em] text-[#F4B842] font-bold mb-1">المنتج</div>
              <div className="font-bold text-[15.5px] text-white">نبض السوق AI</div>
              <div className="text-[11.5px] text-[#6B7480] mt-1">دفعة واحدة — وصول فوري</div>
            </div>
            <div className="text-left">
              <div className="text-[11px] text-[#6B7480] line-through tabular-nums">١٩٩ درهم</div>
              <div className="text-[22px] font-extrabold text-white leading-none" style={{ fontFamily: NABD_FONT_DISPLAY }}>٩٩ درهم</div>
              {(() => {
                const lp = localisedPrice(99, "AED", country);
                return lp ? <div className="text-[10.5px] text-[#F4B842]/85 font-bold mt-1" dir="ltr">{lp}</div> : null;
              })()}
              <div className="text-[10.5px] text-[#F4B842] mt-1">سعر الإطلاق · وفرت ١٠٠ درهم</div>
            </div>
          </div>

          {/* Optional email pre-fill */}
          <div className="mt-5" data-testid="nabd-checkout-email-block">
            <label htmlFor="nabd-checkout-email" className="text-[12.5px] text-[#E8ECEF] font-bold mb-2 flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-[#F4B842]" />
              بريدك الإلكتروني <span className="text-[#6B7480] font-normal">(اختياري — للإيصال)</span>
            </label>
            <input
              id="nabd-checkout-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              data-testid="nabd-checkout-email-input"
              className="w-full rounded-xl bg-[#040608] border border-white/[0.10] focus:border-[#F4B842]/60 focus:outline-none text-[14px] text-white placeholder-[#404853] px-4 py-3.5 transition-colors"
            />
            <div className="mt-1.5 text-[11px] text-[#6B7480]">إذا تركته فارغًا، Stripe يطلبه منك مرة واحدة في صفحة الدفع.</div>
          </div>

          {/* Pre-Stripe trust block + Gulf payment-help bot prompt */}
          <CheckoutTrustBlock
            theme="amber"
            testid="nabd-trust-block"
            onChatOpen={() => setChatOpen(true)}
          />

          {err && (
            <div className="mt-4 text-[12.5px] text-[#EF6B6B] bg-[rgba(239,107,107,0.10)] border border-[rgba(239,107,107,0.30)] rounded-xl px-3.5 py-2.5" data-testid="nabd-checkout-err">
              {err}
            </div>
          )}

          {/* Inline Stripe Payment Element — keeps customer on this branded
              page; Stripe auto-renders Apple Pay / Google Pay / Card. */}
          <div className="mt-6" data-testid="nabd-checkout-inline-payment-wrapper">
            <InlinePayment
              clientSecret={clientSecret}
              orderToken={orderToken}
              amount={99}
              currency="AED"
              currencyLabel="درهم"
              returnPath={orderToken ? `/nabd/success/${orderToken}` : "/nabd"}
              theme="amber"
              testid="nabd-inline-payment"
              buttonLabel="ادفع الآن — ٩٩ درهم"
              beforeConfirm={fireSubmitPixel}
              defaultCountry={country}
            />
          </div>
          {piLoading && !clientSecret && (
            <div className="mt-2 text-center text-[12px] text-[#6B7480]" data-testid="nabd-checkout-loading">جاري تحضير الدفع…</div>
          )}

          {/* Highlighted delivery strip under the CTA */}
          <div
            className="mt-4 rounded-2xl p-3.5 flex items-center justify-center gap-3 border"
            style={{
              background: "linear-gradient(90deg, rgba(0,212,168,0.10) 0%, rgba(244,184,66,0.12) 100%)",
              borderColor: "rgba(244,184,66,0.40)",
            }}
            data-testid="nabd-delivery-strip"
          >
            <div className="text-right leading-tight">
              <div className="text-[10.5px] tracking-[0.18em] text-[#00D4A8] font-bold mb-0.5">أحصل على التطبيق الآن</div>
              <div className="text-[12.5px] text-white font-bold">رابط تحميل مباشر</div>
            </div>
            <div className="w-px h-8 bg-white/15" />
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#F4B842] bg-[#06090F]/70 border border-[#F4B842]/30 rounded-full px-2.5 py-1">
                Android
              </span>
              <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-[#E8ECEF] bg-[#06090F]/70 border border-white/15 rounded-full px-2.5 py-1">
                iOS
              </span>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[11px] text-[#6B7480]">
            <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Stripe آمن</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-[#00D4A8]" /> ضمان ٧ أيام</span>
            <span>·</span>
            <span>Visa · Mastercard · Apple Pay</span>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 rounded-xl px-4 py-3 border" style={{ background: "rgba(0,212,168,0.05)", borderColor: "rgba(0,212,168,0.22)" }}>
          <ShieldCheck className="w-4 h-4 text-[#00D4A8] shrink-0" />
          <div className="text-[12.5px] text-[#E8ECEF] leading-[1.85]">بعد الدفع مباشرة، تحصل على روابط تنزيل التطبيق لكل من Android و iOS.</div>
        </div>
      </main>

      {/* Payment-help bot — opened from the Gulf trust-block CTA. */}
      <PaymentHelpChatbot
        funnel="nabd"
        getPayload={() => ({ email: ((email || "").trim().toLowerCase()) || undefined, visitor_id: nabdVisitorId() })}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
      />
    </div>
  );
}
